/*
 * We are importing a "Virtual Alias" called @clientConfig.
 * Webpack will swap this string with the REAL file path during the build (see vue.config.js).
 * We do this to avoid importing the config of all clients (bad because of SEO cross-contamination).
 */
import config from "@clientConfig";

/**
 * @typedef {{
 *  id: string,
 *  getLastChangeName: () => string,
 * }}
 * UpdateFrequency
 */

/**
 *
 * @typedef {{
 *  updateFrequency: UpdateFrequency,
 *  componentPath: string|undefined,
 *  allowedRoles: Array<string>|undefined,
 *  zoomButtons: Array<any>
 *  genericCardsToShow: Array<string>,
 *  perfStats: Array<string>,
 *  events: Array<{name: string, start: string, end: string}>|undefined,
 *  allowLogScale: boolean|undefined,
 *  defaultLogScale: boolean|undefined,
 *  disableAnalysisBenchmark: boolean|undefined,
 * }}
 * AnalysisDetail
 */

/**
 * A font file (@font-face) or external stylesheet (Google Fonts with format "stylesheet").
 *
 * @typedef {{
 *  fontFamily: string,
 *  src: string,
 *  format: string,
 *  fontWeight: string|undefined,
 *  fontStyle: string|undefined,
 * }}
 * ReportFontFace
 */

/**
 *
 * @typedef {{
 *  family: string,
 *  fontFaces: Array<ReportFontFace>,
 * }}
 * ReportFont
 */

/**
 *
 * @typedef {{
 *  id: string,
 *  langToName: {[key: string]: string},
 *  allowedRoles: Array<string>|undefined,
 *  needLoginToSee: boolean|undefined,
 * }}
 * ReportDetail
 */

/**
 *
 * @typedef {{
 *  orgId: string|undefined,
 *  productId: string,
 *  category: string|Array<string>|undefined,
 *  lockedBehindLogin: boolean|undefined,
 *  storeModule: string,
 *  productName: string,
 *  productShortName: string|undefined,
 *  productColor: any,
 *  productShareClassRoot: string|undefined,
 *  isDefaultShareClass: boolean|undefined,
 *  performanceWatcherPerfometerInternalId: string|undefined,
 *  oldDataEndDate: string|undefined,
 *  oldDataHighlightColor: string|undefined,
 *  showNextSubscriptionAndRedemption: boolean|undefined,
 *  navForAgio: number|undefined,
 *  availableLanguages: Array<string>|undefined,
 *  langsForDocuments: Array<string>|undefined,
 *  allowPriceDataModification: boolean|undefined,
 *  allowHoldingDataModification: boolean|undefined,
 *  allowedRoles: Array<string>|undefined,
 *  showDownloadLastFactsheetAsComponent: boolean|undefined,
 *  timelineData: Array<{
 *    color: string,
 *    icon: string,
 *    title: string,
 *    desc: string,
 *    time: string,
 *  }>|undefined,
 *  withoutOverview: boolean|undefined,
 *  withoutAnalysis: boolean|undefined,
 *  live: AnalysisDetail|undefined,
 *  historical: AnalysisDetail|undefined,
 *  internal: AnalysisDetail|undefined,
 *  useCase: {componentPath: string, redirect: string|undefined}|undefined,
 *  reports: Array<ReportDetail>|undefined,
 *  reportFont: ReportFont|undefined,
 * }}
 * ProductDetail
 */

/**
 *
 * @typedef {{
 *    name: string,
 *    meta: {[key: string]: string | boolean | Array<string>} | undefined,
 *    submenu: Array<NavMenuGroup | NavMenuItem>
 *  }}
 * NavMenuSection
 */

/**
 *
 * @typedef {{
 *    name: string,
 *    slug: string,
 *    meta: {[key: string]: string | boolean | Array<string>} | undefined,
 *    submenu: Array<NavMenuItem>
 *  }}
 * NavMenuGroup
 */

/**
 *
 * @typedef {{
 *    url: string,
 *    name: string,
 *    slug: string,
 *    icon: string
 *  }}
 * NavMenuItem
 */

/**
 *
 * @typedef {{
 *    path: string,
 *    component: () => Promise<any>,
 *    children: Array<RouteChild>
 *  }}
 * Route
 */

/**
 *
 * @typedef {{
 *    path: string,
 *    name: string,
 *    component: () => Promise<any>,
 *    meta: {[key: string]: string | boolean | Array<string>} | undefined,
 *    props: {[key: string]: string | boolean} | undefined,
 *  }}
 * RouteChild
 */

/**
 *
 * @typedef {{
 *  knowledgeBase: boolean,
 *  faq: {faqs: () => Array<{id: number, question: string, ans: string}>},
 *  contact: boolean,
 * }}
 * HelpConfig
 */

/**
 *
 * @type {{
 *  orgName: string,
 *  defaultInvestorProfileCountryCode: string | undefined,
 *  showHomePageHeader: boolean,
 *  logoPath: string,
 *  logoExternalUrl: string | undefined,
 *  redirectButtonNavBar: {url: string, getText: () => string, isInternal: boolean} | undefined,
 *  allowedLoginMethods: Array<string>|undefined,
 *  homePageTable: boolean|undefined,
 *  homePageShowAllProducts: boolean|undefined,
 *  homePageTableColumns: Array<string>|undefined,
 *  hideLogoHomePage: boolean|undefined,
 *  hideCopyrightFooter: boolean|undefined,
 *  hideProducts: boolean|undefined,
 *  background: {path: string, darkRatio: number|undefined, lightRatio: number|undefined},
 *  navBarLogoStyle: string|undefined,
 *  withProductsFilteringAbility: boolean|undefined,
 *  withProductsFilteringAbilityOnHome: boolean|undefined,
 *  numberOfProductsToShow: number|undefined,
 *  numberOfProductsToShowOnHome: number|undefined,
 *  products: Array<ProductDetail>,
 *  categories: Array<{id: string, getName: () => string}>|undefined,
 *  customSections: {getNavMenuItems: () => Array<NavMenuSection>, routes: Array<Route>},
 *  help: HelpConfig,
 *  possibleRoles: Array<string>|undefined,
 *  urlRedirections: Array<{fromUrl: string, toUrl: string}>|undefined,
 *  availableLanguages: Array<string>,
 *  langInUrl: boolean | undefined,
 *  keepLangsDropdownWithLangInUrl: boolean | undefined,
 *  allowedRoles: Array<string>|undefined,
 *  contactEmail: string | undefined,
 *  hideLoginRegisterButtons: boolean | undefined,
 *  hideRegisterButton: boolean | undefined,
 *  followFeatureActivated: boolean | undefined,
 *  investmentProposal: {
 *    allowedRoles: Array<string>,
 *    compositionGroupings: Array<{property: string, getLabel: () => string}>|undefined,
 *    weightOptimization: {
 *      portfolios: Array<{ name: string, defaultCurrency: string, shownByDefault: boolean }>,
 *      defaultAllocations: Array<{
 *        symbol: string,
 *        portfolioNameToAllocation: Record<string, number>,
 *      }>,
 *    },
 *  } | undefined,
 *  disableGoogleAnalytics: boolean | undefined,
 *  disableIndexing: boolean | undefined,
 *  customPrivacyPolicyPath: string | undefined,
 *  enable2FactorAuthentication: boolean | undefined
 *  additionalDisplayOrderMethods: Array<{id: string, getName: () => string, sortFunction: (a: string, b: string) => number}> | undefined
 * }}
 */
export default config;
